import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router";
import axios from 'axios';

import { AppContext } from '../../../../context.js';
import { Download, ChevronUp, ChevronDown, ChevronRight, Minus, CheckCircleFill, ErrorFill, Refresh, Users, Calendar, MarkerPin, File } from "../../../../files/icons/index.js";

import Loading from '../../../components/loading.js';
import Helmet from "../../../components/helmet.js";
import { EditableDatePicker } from "../../../components/datePicker.js";

import { DateToFormat, DatetimeToDate, formatDateYMD } from "../../../../utils/datetime.js";

const TYPE_GROUPS = [
  { key: 'initial',      label: 'Initial Alignment',  color: '#3b82f6' },
  { key: 'reassessment', label: 'Reassessment',        color: '#f97316' },
  { key: 'annual',       label: 'Annual Assessment',   color: '#22c55e' },
  { key: 'unalignment',  label: 'Unalignment',         color: '#a855f7' },
  { key: 'recent',       label: 'Recently Submitted',  color: '#6b7280' },
];

function Paaf() {
  const context = useContext(AppContext);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [exports, setExports] = useState([]);

  const [statistics, setStatistics] = useState(null);


  const [expandedMissingIds, setExpandedMissingIds] = useState(new Set());

  // Selection state for aligned (ready) patients
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [expandedTypes, setExpandedTypes] = useState(new Set());

  // Loading state for export buttons
  const [exportingIds, setExportingIds] = useState(new Set());   // per-row
  const [exportingSelected, setExportingSelected] = useState(false); // batch

  // Pending export awaiting confirmation (some patients have missing fields).
  // Shape: { projectIDs: string[], isSelected: bool, missing: [{ name, fields }] }
  const [confirmExport, setConfirmExport] = useState(null);

  // Choose reassessment reason modal
  // Shape: { project } | null; selectedReason / subReason / other / date are local steps
  const [reasonModal, setReasonModal] = useState(null);
  const [reasonModalSelected, setReasonModalSelected] = useState(null);
  const [reasonModalSubID, setReasonModalSubID] = useState(null);
  const [reasonModalOther, setReasonModalOther] = useState('');
  const [reasonModalDate, setReasonModalDate] = useState('');
  const [reasonModalSaving, setReasonModalSaving] = useState(false);

  function openReasonModal(project) {
    setReasonModal({ project });
    setReasonModalSelected(null);
    setReasonModalSubID(null);
    setReasonModalOther('');
    setReasonModalDate(project.reassessmentOccurredDate || '');
    setReasonModalSaving(false);
  }

  function closeReasonModal() {
    setReasonModal(null);
    setReasonModalSelected(null);
    setReasonModalSubID(null);
    setReasonModalOther('');
    setReasonModalDate('');
    setReasonModalSaving(false);
  }

  function selectReasonInModal(reason) {
    if (!reasonModalDate) {
      return;
    }
    if (reason.subReasons && reason.subReasons.length > 0) {
      setReasonModalSelected(reason);
      setReasonModalSubID(null);
      setReasonModalOther('');
      return;
    }
    saveReassessmentReason(reason.title, null, null);
  }

  function confirmReasonSubStep() {
    if (!reasonModalSelected || !reasonModalSubID || !reasonModalDate) return;
    const sub = reasonModalSelected.subReasons.find(sr => sr.id === reasonModalSubID);
    if (!sub) return;
    const isOther = sub.title === 'Other (please specify)';
    if (isOther && !reasonModalOther.trim()) return;
    saveReassessmentReason(
      reasonModalSelected.title,
      sub.title,
      isOther ? reasonModalOther.trim() : null,
    );
  }

  function saveReassessmentReason(reassessmentReason, changeCaregiverReason, changeCaregiverReasonOther) {
    if (!reasonModal || !reasonModal.project || !reasonModalDate) return;
    const projectId = reasonModal.project.id;
    const occurredDate = reasonModalDate;
    setReasonModalSaving(true);
    const body = {
      id: reasonModal.project.patientID,
      reassessmentReason,
      reassessmentOccurredDate: occurredDate,
    };
    if (changeCaregiverReason) {
      body.changeCaregiverReason = changeCaregiverReason;
    }
    if (changeCaregiverReasonOther) {
      body.changeCaregiverReasonOther = changeCaregiverReasonOther;
    }
    axios.post('/api/admin/patients/updateReassessmentReason', body).then(() => {
      // Update the reassessment row immediately, then reconcile with server
      // (missing fields / ready flags are computed server-side).
      setStatistics((prev) => {
        if (!prev || !prev.projects) return prev;
        return {
          ...prev,
          projects: prev.projects.map((p) => {
            if (p.id !== projectId) return p;
            const drop = new Set(['Reassessment Reason', 'Reassessment Occurred Date']);
            if (changeCaregiverReason) drop.add('Change in Caregiver Status');
            return {
              ...p,
              reassessmentReason,
              changeCaregiverReason: changeCaregiverReason || null,
              changeCaregiverReasonOther: changeCaregiverReasonOther || null,
              reassessmentOccurredDate: occurredDate,
              missingFields: (p.missingFields || []).filter((f) => !drop.has(f)),
            };
          }),
        };
      });
      closeReasonModal();
      return loadStatistics(false);
    }).catch((e) => {
      context.addError(e);
      setReasonModalSaving(false);
    });
  }

  function toggleTypeGroup(key) {
    setExpandedTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  }

  function toggleMissingExpansion(projectID) {
    setExpandedMissingIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectID)) {
        next.delete(projectID);
      } else {
        next.add(projectID);
      }
      return next;
    });
  }

  function loadStatistics(showSpinner = true) {
    let u = showSpinner ? context.addSpiner() : null;
    return axios.post('/api/admin/analytics/paaf/statistics', {}).then((statistics) => {
      setStatistics(statistics.data);
      setExports(statistics.data.exports);
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      if (u) context.removeSpiner(u);
    });
  }

  useEffect(() => {
    loadStatistics();
  }, []);

  function getLastExportDate() {
    if (!exports || exports.length === 0) {
      return null;
    }

    return exports[exports.length - 1].exportedAt;
  }

  const downloadFile = (fileID, fileName, fileExtension) => {
    axios.get('/api/admin/files/' + fileID, {
      responseType: 'blob'
    }).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName + "." + fileExtension);
      document.body.appendChild(link);
      link.click();
    }).catch(error => {
      //setImageSrc(Profile);
    });
  }

  function renderExportHistory() {
    if (!exports || exports.length === 0) {
      return (
        <div className="card-header sm">
          <div className="typo-normal-150">No export history available.</div>
        </div>
      );
    }

    return [...exports].reverse().map((exportItem, index) => {
      return (
        <div className={"card-header clickable sm " + (exports.length === index + 1 ? "last": "")} key={index}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="w-25">
              <div className="typo-strong-150">Exported at: {DateToFormat(DatetimeToDate(exportItem.exportedAt), "MM/DD/YYYY AMPM")}</div>
            </div>
            <div className="w-25">
              <div className="typo-normal-150">Number of patients: {exportItem.projects ? exportItem.projects.length : "n/a"}</div>
            </div>
            <div className="w-50 text-end">
              <div className="item typo-normal-150" onClick={() => downloadFile(exportItem.fileID, exportItem.fileName, exportItem.fileExtension)}>
                Download <Download height={16} width={16} color={'grey'} />
              </div>
            </div>
          </div>
        </div>
      );
    });
  }

  function toggleSelected(projectId) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }

function getSelectableIdsForGroup(key) {
    if (!statistics || !statistics.projects) return [];
    return statistics.projects
      .filter(p => p.type === key)
      .filter(p => !p.submitted)
      .filter(p => !recentlySubmittedIds.has(p.id))
      .map(p => p.id);
  }

  function getMissingInfo(projectIDs) {
    if (!statistics || !statistics.projects) return [];
    const map = Object.fromEntries(statistics.projects.map(p => [p.id, p]));
    return projectIDs
      .map(id => map[id])
      .filter(p => p && p.missingFields && p.missingFields.length > 0)
      .map(p => ({ name: p.firstName + ' ' + p.lastName, fields: p.missingFields }));
  }

  function getNotReadyInfo(projectIDs) {
    if (!statistics || !statistics.projects) return [];
    const map = Object.fromEntries(statistics.projects.map(p => [p.id, p]));
    return projectIDs
      .map(id => map[id])
      // Instrument/reason incomplete only — patients with missing PAAF fields
      // are covered by getMissingInfo so they are not listed twice.
      .filter(p => p && p.export !== true && (!p.missingFields || p.missingFields.length === 0))
      .map(p => ({ name: p.firstName + ' ' + p.lastName, fields: p.missingFields || [] }));
  }

  function requestExport(projectIDs, isSelected = false) {
    const notReady = getNotReadyInfo(projectIDs);
    const missing = getMissingInfo(projectIDs);
    if (notReady.length > 0 || missing.length > 0) {
      setConfirmExport({ projectIDs, isSelected, notReady, missing });
    } else {
      downloadExcel(projectIDs, isSelected);
    }
  }

  function confirmExportProceed() {
    if (!confirmExport) return;
    const { projectIDs, isSelected } = confirmExport;
    setConfirmExport(null);
    downloadExcel(projectIDs, isSelected);
  }

  function isAllSelectedForGroup(key) {
    const ids = getSelectableIdsForGroup(key);
    if (ids.length === 0) return false;
    return ids.every(id => selectedIds.has(id));
  }

  function toggleSelectAllForGroup(key) {
    const ids = getSelectableIdsForGroup(key);
    if (isAllSelectedForGroup(key)) {
      setSelectedIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    } else {
      setSelectedIds(prev => new Set([...prev, ...ids]));
    }
  }

  function getRecentSelectableIds() {
    return recentlySubmitted.map(i => i.id);
  }

  function isAllRecentSelected() {
    const ids = getRecentSelectableIds();
    if (ids.length === 0) return false;
    return ids.every(id => selectedIds.has(id));
  }

  function toggleSelectAllRecent() {
    const ids = getRecentSelectableIds();
    if (isAllRecentSelected()) {
      setSelectedIds(prev => { const n = new Set(prev); ids.forEach(id => n.delete(id)); return n; });
    } else {
      setSelectedIds(prev => new Set([...prev, ...ids]));
    }
  }

  function renderProjectRow(project, isLast, showReason) {
    const aligned = project.export === true && (!project.missingFields || project.missingFields.length === 0);
    const isSelectable = true;
    const isSelected = selectedIds.has(project.id);
    const patientCol = showReason ? 'col-xs-3' : 'col-xs-4';
    const missingCol = showReason ? 'col-xs-2' : 'col-xs-3';

    return (
      <div className={"card-header sm " + (isLast ? "last" : "")} key={project.id}>
        <div className="row align-items-center">
          <div className="col-xs-1 d-flex align-items-center justify-content-center" style={{ minWidth: '32px', maxWidth: '32px' }}>
            <input
              type="checkbox"
              checked={isSelected}
              disabled={!isSelectable}
              onChange={() => toggleSelected(project.id)}
              onClick={(e) => e.stopPropagation()}
              style={{ cursor: isSelectable ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }}
            />
          </div>

          <div className={patientCol}>
            <div
              className="typo-strong-175 link"
              onClick={() => navigate(`/patient/${project.patientID}/card`)}
            >
              {project.firstName + " " + project.lastName}
            </div>
          </div>

          {showReason && (
            <div className="col-xs-2">
              {project.reassessmentReason ? (
                <>
                  <div className="typo-normal-125" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={project.reassessmentReason}>
                    {project.reassessmentReason}
                  </div>
                  {project.changeCaregiverReason && (
                    <div className="typo-normal-125" style={{ color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={project.changeCaregiverReason}>
                      {project.changeCaregiverReason}
                    </div>
                  )}
                  {project.reassessmentOccurredDate && (
                    <div className="typo-normal-125" style={{ color: '#888' }}>
                      {project.reassessmentOccurredDate}
                    </div>
                  )}
                </>
              ) : (
                <div className="typo-normal-125" style={{ color: '#ccc' }}>—</div>
              )}
              <div
                className="item typo-normal-125 mt-1"
                style={{ color: '#f97316', whiteSpace: 'nowrap' }}
                onClick={(e) => { e.stopPropagation(); openReasonModal(project); }}
              >
                {project.reassessmentReason ? 'Change' : 'Choose reason'}
              </div>
            </div>
          )}

          <div className={missingCol}>
            {project.missingFields && project.missingFields.length > 0 && (
              <>
                <div
                  className="typo-normal-125 clickable d-inline-flex align-items-center"
                  style={{ color: 'darkorange' }}
                  onClick={(e) => { e.stopPropagation(); toggleMissingExpansion(project.id); }}
                >
                  <ErrorFill height={12} width={12} color={'darkorange'} />
                  <span className="ms-1 me-1">{project.missingFields.length} missing</span>
                  {expandedMissingIds.has(project.id)
                    ? <ChevronUp height={12} width={12} color={'darkorange'} />
                    : <ChevronDown height={12} width={12} color={'darkorange'} />}
                </div>
                {expandedMissingIds.has(project.id) && (
                  <ul className="typo-normal-125 mt-1 mb-0 ps-3" style={{ color: 'darkorange' }}>
                    {project.missingFields.map((field, fieldIndex) => (
                      <li key={fieldIndex}>{field}</li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div className="col-xs-3">
            <div className="row">
              <div className="col-xs-4 text-center">
                {renderCheckIcon(!!project.cdrRequired, project.cdr || project.fast)}
              </div>
              <div className="col-xs-4 text-center">
                {renderCheckIcon(!!project.zbiRequired, project.zbi)}
              </div>
              <div className="col-xs-4 text-center">
                {renderCheckIcon(project.type === 'initial' || project.type === 'annual', project.promis)}
              </div>
            </div>
          </div>

          <div className="col-xs-1 text-end" style={{ minWidth: '80px', maxWidth: '80px' }}>
            <div
              className="item typo-normal-125"
              onClick={(e) => { e.stopPropagation(); if (!exportingIds.has(project.id)) requestExport([project.id]); }}
              style={{ whiteSpace: 'nowrap', opacity: exportingIds.has(project.id) ? 0.6 : 1, color: aligned ? 'grey' : 'darkorange' }}
            >
              {exportingIds.has(project.id)
                ? <><span className="spin-icon"><Refresh height={14} width={14} color={aligned ? 'grey' : 'darkorange'} /></span> Exporting…</>
                : <>Export <Download height={14} width={14} color={aligned ? 'grey' : 'darkorange'} /></>
              }
            </div>
            {!aligned && (
              <div className="typo-normal-125" style={{ color: '#ccc', whiteSpace: 'nowrap' }}>Not Ready</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  function renderProjects() {
    return TYPE_GROUPS.map(({ key, label, color }) => {
      const isExpanded = expandedTypes.has(key);

      // Recently submitted: patient + type combos exported in the last 90 days
      if (key === 'recent') {
        return (
          <div key={key}>
            <div
              className={"card-header sm clickable" + (!isExpanded ? " last" : "")}
              onClick={() => toggleTypeGroup(key)}
            >
              <div className="d-flex align-items-center">
                {isExpanded
                  ? <ChevronDown height={16} width={16} color={color} />
                  : <ChevronRight height={16} width={16} color={color} />
                }
                <span className="typo-strong-175 ms-2" style={{ color }}>{label}</span>
                <span
                  className="ms-2 typo-normal-125"
                  style={{ background: color + '22', color, borderRadius: '999px', padding: '1px 8px' }}
                >
                  {recentlySubmitted.length}
                </span>
              </div>
            </div>
            {isExpanded && recentlySubmitted.length === 0 && (
              <div className="card-header sm last">
                <div className="typo-normal-150">No patients submitted in the last 90 days.</div>
              </div>
            )}
            {isExpanded && recentlySubmitted.length > 0 && (
              <div className="card-header sm">
                <div className="row align-items-center">
                  <div className="col-xs-1 d-flex align-items-center justify-content-center" style={{ minWidth: '32px', maxWidth: '32px' }}>
                    <input
                      type="checkbox"
                      checked={isAllRecentSelected()}
                      disabled={getRecentSelectableIds().length === 0}
                      onChange={toggleSelectAllRecent}
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: getRecentSelectableIds().length === 0 ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                      title={getRecentSelectableIds().length === 0 ? 'No selectable patients' : (isAllRecentSelected() ? 'Deselect all' : 'Select all')}
                    />
                  </div>
                  <div className="col-xs-3"><div className="typo-strong-150">Patient</div></div>
                  <div className="col-xs-3"><div className="typo-strong-150">Type</div></div>
                  <div className="col-xs-3"><div className="typo-strong-150">Submitted</div></div>
                  <div className="col-xs-1" style={{ minWidth: '80px', maxWidth: '80px' }}></div>
                </div>
              </div>
            )}
            {isExpanded && recentlySubmitted.map((item, i) => {
              const group = typeGroupByKey[item.typeKey];
              const itemColor = group ? group.color : '#888';
              const itemLabel = group ? group.label : item.typeKey;
              const isSelected = selectedIds.has(item.id);
              return (
                <div className={"card-header sm " + (i === recentlySubmitted.length - 1 ? "last" : "")} key={item.id + ':' + item.typeKey}>
                  <div className="row align-items-center">
                    <div className="col-xs-1 d-flex align-items-center justify-content-center" style={{ minWidth: '32px', maxWidth: '32px' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelected(item.id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </div>
                    <div className="col-xs-3">
                      <div className="typo-strong-175 link" onClick={() => navigate(`/patient/${item.patientID}/card`)}>
                        {item.firstName + " " + item.lastName}
                      </div>
                    </div>
                    <div className="col-xs-3">
                      <span
                        className="typo-normal-125"
                        style={{ background: itemColor + '22', color: itemColor, borderRadius: '999px', padding: '1px 8px' }}
                      >
                        {itemLabel}
                      </span>
                    </div>
                    <div className="col-xs-3">
                      <div className="typo-normal-150">{DateToFormat(DatetimeToDate(item.exportedAt), "MM/DD/YYYY AMPM")}</div>
                    </div>
                    <div className="col-xs-1 text-end" style={{ minWidth: '80px', maxWidth: '80px' }}>
                      <div
                        className="item grey typo-normal-125"
                        onClick={(e) => { e.stopPropagation(); if (!exportingIds.has(item.id)) requestExport([item.id]); }}
                        style={{ whiteSpace: 'nowrap', opacity: exportingIds.has(item.id) ? 0.6 : 1 }}
                      >
                        {exportingIds.has(item.id)
                          ? <><span className="spin-icon"><Refresh height={14} width={14} color={'grey'} /></span> Exporting…</>
                          : <>Export <Download height={14} width={14} color={'grey'} /></>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }

      const projects = statistics.projects
        .filter(p => p.type === key)
        .filter(p => !p.submitted)
        // Also hide optimistic/ready exports that landed in Recently Submitted
        // before statistics.submitted has refreshed (and for initial/unalignment
        // until the server marks submitted).
        .filter(p => !recentlySubmittedIds.has(p.id))
        .sort((a, b) => {
          const aReady = a.export && (!a.missingFields || a.missingFields.length === 0);
          const bReady = b.export && (!b.missingFields || b.missingFields.length === 0);
          if (aReady !== bReady) return aReady ? -1 : 1;
          if (a.firstName.toLowerCase() < b.firstName.toLowerCase()) return -1;
          if (a.firstName.toLowerCase() > b.firstName.toLowerCase()) return 1;
          return 0;
        });

      return (
        <div key={key}>
          <div
            className="card-header sm clickable"
            onClick={() => toggleTypeGroup(key)}
          >
            <div className="d-flex align-items-center">
              {isExpanded
                ? <ChevronDown height={16} width={16} color={color} />
                : <ChevronRight height={16} width={16} color={color} />
              }
              <span className="typo-strong-175 ms-2" style={{ color }}>{label}</span>
              <span
                className="ms-2 typo-normal-125"
                style={{
                  background: color + '22',
                  color,
                  borderRadius: '999px',
                  padding: '1px 8px',
                }}
              >
                {projects.filter(p => p.export && (!p.missingFields || p.missingFields.length === 0)).length}
              </span>
            </div>
          </div>
          {isExpanded && projects.length === 0 && (
            <div className="card-header sm">
              <div className="typo-normal-150">No patients in this category.</div>
            </div>
          )}
          {isExpanded && projects.length > 0 && (
            <div className="card-header sm">
              <div className="row align-items-center">
                <div className="col-xs-1 d-flex align-items-center justify-content-center" style={{ minWidth: '32px', maxWidth: '32px' }}>
                  <input
                    type="checkbox"
                    checked={isAllSelectedForGroup(key)}
                    disabled={getSelectableIdsForGroup(key).length === 0}
                    onChange={() => toggleSelectAllForGroup(key)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ cursor: getSelectableIdsForGroup(key).length === 0 ? 'not-allowed' : 'pointer', width: '16px', height: '16px' }}
                    title={getSelectableIdsForGroup(key).length === 0 ? 'No selectable patients' : (isAllSelectedForGroup(key) ? 'Deselect all' : 'Select all in group')}
                  />
                </div>
                <div className={key === 'reassessment' ? 'col-xs-3' : 'col-xs-4'}><div className="typo-strong-150">Patient</div></div>
                {key === 'reassessment' && (
                  <div className="col-xs-2"><div className="typo-strong-150">Reason</div></div>
                )}
                <div className={key === 'reassessment' ? 'col-xs-2' : 'col-xs-3'}><div className="typo-strong-150">Missing Info</div></div>
                <div className="col-xs-3">
                  <div className="row">
                    <div className="col-xs-4 text-center"><div className="typo-strong-150">CDR / FAST</div></div>
                    <div className="col-xs-4 text-center"><div className="typo-strong-150">ZBI</div></div>
                    <div className="col-xs-4 text-center"><div className="typo-strong-150">PROMIS</div></div>
                  </div>
                </div>
                <div className="col-xs-1" style={{ minWidth: '80px', maxWidth: '80px' }}></div>
              </div>
            </div>
          )}
          {isExpanded && projects.length > 0 && projects.map((project) =>
            renderProjectRow(project, false, key === 'reassessment')
          )}
        </div>
      );
    });
  }

  function renderCheckIcon(required, condition) {
    if (!required) {
      return <Minus height={16} width={16} color={'grey'} />;
    }

    return condition ? (
      <CheckCircleFill height={16} width={16} color={'green'} />
    ) : (
      <ErrorFill height={16} width={16} color={'firebrick'} />
    );
  }

  // Function to download Excel file
  // projectIDs: array of project ID strings to export; empty/undefined = export all aligned
  // isSelected: true when triggered by the "Export Selected" batch button
  const downloadExcel = async (projectIDs, isSelected = false) => {
    // Set loading state
    if (isSelected) {
      setExportingSelected(true);
    } else {
      setExportingIds(prev => new Set([...prev, ...projectIDs]));
    }

    try {
      const body = projectIDs && projectIDs.length > 0 ? { projectIDs } : {};

      const response = await axios.post('/api/admin/analytics/paaf/export', body, {
        responseType: 'blob'
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Extract filename from server response headers
      let fileName = 'paaf_export.xlsx'; // fallback filename
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename=(.+)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/"/g, ''); // Remove quotes if present
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName; // Use filename from server
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Optimistically move only the fully ready (export-ready, no missing
      // fields) patients into "Recently Submitted" right away, then
      // reconcile with the server so the History panel gets the real file
      // record. Not-ready/incomplete patients must stay in their group.
      const projectMap = Object.fromEntries((statistics.projects || []).map(p => [p.id, p]));
      const readyProjectIDs = projectIDs.filter(id => {
        const p = projectMap[id];
        return p && p.export === true && (!p.missingFields || p.missingFields.length === 0);
      });
      const readyRows = readyProjectIDs.map(id => {
        const p = projectMap[id];
        return { projectID: id, patientID: p.patientID, type: p.type };
      });
      setExports(prev => [...prev, { exportedAt: new Date().toISOString(), projects: projectIDs, readyProjects: readyProjectIDs, readyRows }]);
      setSelectedIds(prev => {
        const next = new Set(prev);
        projectIDs.forEach(id => next.delete(id));
        return next;
      });
      loadStatistics(false);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
    } finally {
      // Clear loading state
      if (isSelected) {
        setExportingSelected(false);
      } else {
        setExportingIds(prev => {
          const next = new Set(prev);
          projectIDs.forEach(id => next.delete(id));
          return next;
        });
      }
    }
  };

  if (loading) {
    return (
      <>
        <Helmet title="Analytics | PAAF Export" />
        <Loading />
      </>
    );
  }

  function exportHistoryType(exportItem, p, ids) {
    const rows = exportItem.readyRows || [];
    const row = rows.find(r => r.patientID === p.patientID || ids.has(r.projectID));
    if (row && row.type) return row.type;
    const preIds = new Set(p.preAlignmentProjectIDs || []);
    if ((exportItem.readyProjects || []).some(id => ids.has(id) && preIds.has(id))) {
      return 'initial';
    }
    if (p.type) return p.type;
    return '';
  }

  function getRecentlySubmitted() {
    if (!exports || exports.length === 0 || !statistics || !statistics.projects) return [];
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    const items = [];
    for (const p of statistics.projects) {
      const ids = new Set([p.id, ...(p.projectIDs || [])]);
      let best = null;
      for (const exportItem of exports) {
        const readyIds = exportItem.readyProjects || [];
        const readyRows = exportItem.readyRows || [];
        const matchesReady = readyIds.some(id => ids.has(id));
        const matchesRow = readyRows.some(r => r.patientID === p.patientID || ids.has(r.projectID));
        if ((!matchesReady && !matchesRow) || new Date(exportItem.exportedAt).getTime() < cutoff) continue;
        const exportedAt = new Date(exportItem.exportedAt).getTime();
        // Unalignment reuses the same project ID. Exports from before the
        // cancel are a different row type and must not land in Recently
        // Submitted (or hide the patient from the Unalignment section).
        if (p.type === 'unalignment' && p.closedAt) {
          const closedAt = new Date(p.closedAt).getTime();
          if (!Number.isNaN(closedAt) && exportedAt < closedAt) continue;
        }
        if (!best || exportedAt > new Date(best.exportedAt).getTime()) best = exportItem;
      }
      if (!best) continue;
      const typeKey = exportHistoryType(best, p, ids);
      items.push({
        id: p.id,
        patientID: p.patientID,
        firstName: p.firstName,
        lastName: p.lastName,
        typeKey,
        exportedAt: best.exportedAt,
      });
    }
    return items.sort((a, b) => new Date(b.exportedAt) - new Date(a.exportedAt));
  }

  const recentlySubmitted = getRecentlySubmitted();
  const recentlySubmittedIds = new Set(recentlySubmitted.map(i => i.id));

  const isFullyReady = (p) => p.export && (!p.missingFields || p.missingFields.length === 0);

  const readyByType = {
    initial:      statistics.projects.filter(p => p.type === 'initial' && isFullyReady(p) && !p.submitted && !recentlySubmittedIds.has(p.id)).length,
    reassessment: statistics.projects.filter(p => p.type === 'reassessment' && isFullyReady(p) && !p.submitted && !recentlySubmittedIds.has(p.id)).length,
    annual:       statistics.projects.filter(p => p.type === 'annual' && isFullyReady(p) && !p.submitted && !recentlySubmittedIds.has(p.id)).length,
    unalignment:  statistics.projects.filter(p => p.type === 'unalignment' && isFullyReady(p) && !p.submitted && !recentlySubmittedIds.has(p.id)).length,
  };

  const typeGroupByKey = Object.fromEntries(TYPE_GROUPS.map(g => [g.key, g]));

  return (
    <>
      <Helmet title="Analytics | PAAF Export" />

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="">
                <div className="typo-hero-small">PAAF Export</div>
                <div className="typo-normal-175">The export feature is ready to use. If you notice any issues, please let us know</div>
              </div>
              <div className="w-25 text-end">
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex" style={{ gap: '16px' }}>
              <div className="card flex-fill">
                <div className="card-content sm">
                  <div className="d-flex align-items-center mb-2">
                    <Users height={28} width={28} color={'#3b82f6'} />
                  </div>
                  <div className="typo-strong-175 mb-1">Initial Alignments</div>
                  <div className="mb-1">
                    <span className="typo-strong-300">{readyByType.initial}</span>
                    <span className="typo-normal-175 ms-1">patient{readyByType.initial !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="typo-normal-150" style={{ color: '#888' }}>Eligible never; align once</div>
                </div>
              </div>
              <div className="card flex-fill">
                <div className="card-content sm">
                  <div className="d-flex align-items-center mb-2">
                    <Refresh height={28} width={28} color={'#f97316'} />
                  </div>
                  <div className="typo-strong-175 mb-1">Reassessments</div>
                  <div className="mb-1">
                    <span className="typo-strong-300">{readyByType.reassessment}</span>
                    <span className="typo-normal-175 ms-1">patient{readyByType.reassessment !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="typo-normal-150" style={{ color: '#888' }}>Window: days 180–305 since last assessment</div>
                </div>
              </div>
              <div className="card flex-fill">
                <div className="card-content sm">
                  <div className="d-flex align-items-center mb-2">
                    <Calendar height={28} width={28} color={'#22c55e'} />
                  </div>
                  <div className="typo-strong-175 mb-1">Annual Assessments</div>
                  <div className="mb-1">
                    <span className="typo-strong-300">{readyByType.annual}</span>
                    <span className="typo-normal-175 ms-1">patient{readyByType.annual !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="typo-normal-150" style={{ color: '#888' }}>Window: opens day 306 (overdue past day 425)</div>
                </div>
              </div>
              <div className="card flex-fill">
                <div className="card-content sm">
                  <div className="d-flex align-items-center mb-2">
                    <MarkerPin height={28} width={28} color={'#a855f7'} />
                  </div>
                  <div className="typo-strong-175 mb-1">Unalignments</div>
                  <div className="mb-1">
                    <span className="typo-strong-300">{readyByType.unalignment}</span>
                    <span className="typo-normal-175 ms-1">patient{readyByType.unalignment !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="typo-normal-150" style={{ color: '#888' }}>When patient no longer eligible</div>
                </div>
              </div>
              <div className="card flex-fill">
                <div className="card-content sm">
                  <div className="d-flex align-items-center mb-2">
                    <File height={28} width={28} color={'#6b7280'} />
                  </div>
                  <div className="typo-strong-175 mb-1">Recently Submitted</div>
                  <div className="mb-1">
                    <span className="typo-strong-300">{recentlySubmitted.length}</span>
                    <span className="typo-normal-175 ms-1">patient{recentlySubmitted.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="typo-normal-150" style={{ color: '#888' }}>Exported in last 90 days</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">

            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="w-75">
                    <div className="typo-strong-175">Last export: {getLastExportDate() ? DateToFormat(DatetimeToDate(getLastExportDate()), "MM/DD/YYYY AMPM") : 'N/A'}</div>
                    <div className="typo-normal-150">The information below reflects data from that point onward.</div>
                  </div>

                  <div className="item grey typo-normal-150" onClick={() => setHistoryExpanded(!historyExpanded)}>History {historyExpanded ? <ChevronUp height={16} width={16} color={'grey'} /> : <ChevronDown height={16} width={16} color={'grey'} />}</div>
                </div>
              </div>
              {historyExpanded && renderExportHistory()}
            </div>

            <div className="card mb-2">
              <div className="card-header sm">
                {/* Export Selected action bar */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="typo-normal-150" style={{ color: selectedIds.size > 0 ? 'inherit' : '#aaa' }}>
                    {selectedIds.size > 0 ? `${selectedIds.size} patient${selectedIds.size === 1 ? '' : 's'} selected` : 'Select patients to export'}
                  </div>
                  <div
                    className={"item grey typo-normal-150" + (selectedIds.size === 0 ? " disabled" : "")}
                    onClick={selectedIds.size > 0 && !exportingSelected ? () => requestExport([...selectedIds], true) : undefined}
                    style={{ cursor: selectedIds.size === 0 || exportingSelected ? 'not-allowed' : 'pointer', opacity: selectedIds.size === 0 ? 0.4 : exportingSelected ? 0.6 : 1 }}
                  >
                    {exportingSelected
                      ? <><span className="spin-icon"><Refresh height={16} width={16} color={'grey'} /></span> Exporting…</>
                      : <>Export Selected <Download height={16} width={16} color={'grey'} /></>
                    }
                  </div>
                </div>

              </div>
              {renderProjects()}

              {(!statistics || statistics.projects.length === 0) && (
                <div className="card-header sm last">
                  <div className="typo-normal-150">No patients found.</div>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {confirmExport && (
        <div className="fixed-bottom modal-bg" onClick={() => setConfirmExport(null)}>
          <div className="container-fluid" style={{ paddingTop: '5%' }}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-6 col-lg-offset-3">
                <div className="card shadowmore" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <div className="card-header" style={{ flexShrink: 0 }}>
                    {confirmExport.notReady.length > 0 ? (
                      <>
                        <span className="typo-header-3xLarge">Export not-ready patient{confirmExport.notReady.length === 1 ? '' : 's'}?</span><br />
                        <span className="typo-normal-175">
                          {confirmExport.notReady.length} of the patient{confirmExport.notReady.length === 1 ? '' : 's'} you're exporting{' '}
                          {confirmExport.notReady.length === 1 ? 'is' : 'are'} not ready — required assessments or fields are incomplete.
                          Those values will be blank in the export.
                          {confirmExport.missing.length > 0 && ' Additional patients are also missing required PAAF fields.'}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="typo-header-3xLarge">Export with missing fields?</span><br />
                        <span className="typo-normal-175">
                          {confirmExport.missing.length} of the patient{confirmExport.missing.length === 1 ? '' : 's'} you're exporting{' '}
                          {confirmExport.missing.length === 1 ? 'is' : 'are'} missing required PAAF fields.
                          These fields will be blank in the export.
                        </span>
                      </>
                    )}
                  </div>
                  <div className="card-content" style={{ overflowY: 'auto', flex: 1 }}>
                    {confirmExport.notReady.length > 0 && (
                      <>
                        <div className="typo-strong-150 mb-1" style={{ color: 'firebrick' }}>Not ready</div>
                        {confirmExport.notReady.map((m, i) => (
                          <div key={'nr-' + i} className="mb-2">
                            <div className="typo-strong-175">{m.name}</div>
                            {m.fields.length > 0 && (
                              <ul className="typo-normal-125 mb-0 ps-3" style={{ color: 'firebrick' }}>
                                {m.fields.map((f, fi) => <li key={fi}>{f}</li>)}
                              </ul>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    {confirmExport.missing.length > 0 && (
                      <>
                        <div className="typo-strong-150 mb-1" style={{ color: 'darkorange' }}>Missing fields</div>
                        {confirmExport.missing.map((m, i) => (
                          <div key={'mf-' + i} className="mb-2">
                            <div className="typo-strong-175">{m.name}</div>
                            <ul className="typo-normal-125 mb-0 ps-3" style={{ color: 'darkorange' }}>
                              {m.fields.map((f, fi) => <li key={fi}>{f}</li>)}
                            </ul>
                          </div>
                        ))}
                      </>
                    )}
                    <div className="d-flex justify-content-end mt-3" style={{ gap: '8px' }}>
                      <div className="item grey typo-normal-150" onClick={() => setConfirmExport(null)}>Cancel</div>
                      <div className="item grey typo-normal-150" onClick={confirmExportProceed}>
                        Export anyway <Download height={16} width={16} color={'grey'} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {reasonModal && (
        <div className="fixed-bottom modal-bg" onClick={closeReasonModal}>
          <div className="container-fluid" style={{ paddingTop: '5%' }}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <div className="card-header" style={{ flexShrink: 0 }}>
                    <span className="typo-header-3xLarge">Choose reassessment reason</span><br />
                    <span className="typo-normal-175">
                      {reasonModal.project.firstName} {reasonModal.project.lastName}
                      {reasonModalSelected && reasonModalSelected.subReasons && reasonModalSelected.subReasons.length > 0
                        ? ' — select caregiver status'
                        : ''}
                    </span>
                  </div>
                  <div className="card-content" style={{ overflowY: 'auto', flex: 1 }}>
                    <div className="mb-3">
                      <div className="typo-strong-150 mb-1">Date the reason for Reassessment occurred</div>
                      <EditableDatePicker
                        date={reasonModalDate}
                        onChange={(d) => setReasonModalDate(d ? formatDateYMD(d) : '')}
                        disabled={reasonModalSaving}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                      />
                      {!reasonModalDate && (
                        <div className="typo-normal-125 mt-1" style={{ color: 'darkorange' }}>
                          Required before selecting a reason
                        </div>
                      )}
                    </div>

                    {(!reasonModalSelected || !reasonModalSelected.subReasons || reasonModalSelected.subReasons.length === 0) && (
                      <>
                        {(statistics.reassessmentReasons || []).map((reason) => (
                          <div
                            key={reason.id}
                            className="side-item small typo-medium-150 mt-3"
                            onClick={() => !reasonModalSaving && selectReasonInModal(reason)}
                            style={{ opacity: reasonModalSaving || !reasonModalDate ? 0.5 : 1, cursor: reasonModalDate ? 'pointer' : 'not-allowed' }}
                          >
                            {reason.title}
                          </div>
                        ))}
                        {(!statistics.reassessmentReasons || statistics.reassessmentReasons.length === 0) && (
                          <div className="typo-normal-150">No reassessment reasons configured.</div>
                        )}
                      </>
                    )}

                    {reasonModalSelected && reasonModalSelected.subReasons && reasonModalSelected.subReasons.length > 0 && (
                      <>
                        <div
                          className="item typo-normal-125 mb-2"
                          style={{ color: '#888' }}
                          onClick={() => { setReasonModalSelected(null); setReasonModalSubID(null); setReasonModalOther(''); }}
                        >
                          ← Back
                        </div>
                        {reasonModalSelected.subReasons.map((sr) => (
                          <div
                            key={sr.id}
                            className={`side-item small typo-medium-150${reasonModalSubID === sr.id ? ' active' : ''}`}
                            onClick={() => { setReasonModalSubID(sr.id); setReasonModalOther(''); }}
                          >
                            {sr.title}
                          </div>
                        ))}
                        {reasonModalSubID && reasonModalSelected.subReasons.find(sr => sr.id === reasonModalSubID)?.title === 'Other (please specify)' && (
                          <div className="mt-2">
                            <input
                              type="text"
                              className="input borderless typo-normal-150 w-100"
                              placeholder="Please specify..."
                              value={reasonModalOther}
                              onChange={(e) => setReasonModalOther(e.target.value)}
                              autoFocus
                            />
                          </div>
                        )}
                        <div className="d-flex justify-content-end mt-3" style={{ gap: '8px' }}>
                          <div className="item grey typo-normal-150" onClick={closeReasonModal}>Cancel</div>
                          <div
                            className="item typo-normal-150"
                            style={{
                              color: (!reasonModalDate || !reasonModalSubID || reasonModalSaving || (reasonModalSelected.subReasons.find(sr => sr.id === reasonModalSubID)?.title === 'Other (please specify)' && !reasonModalOther.trim())) ? '#ccc' : '#3b82f6',
                              cursor: (!reasonModalDate || !reasonModalSubID || reasonModalSaving || (reasonModalSelected.subReasons.find(sr => sr.id === reasonModalSubID)?.title === 'Other (please specify)' && !reasonModalOther.trim())) ? 'not-allowed' : 'pointer',
                            }}
                            onClick={() => {
                              if (reasonModalSaving || !reasonModalDate) return;
                              if (!reasonModalSubID) return;
                              const sub = reasonModalSelected.subReasons.find(sr => sr.id === reasonModalSubID);
                              if (!sub) return;
                              if (sub.title === 'Other (please specify)' && !reasonModalOther.trim()) return;
                              confirmReasonSubStep();
                            }}
                          >
                            {reasonModalSaving ? 'Saving…' : 'Confirm'}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Paaf;
